import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { EntityType } from '@frontend/common';
import { IGroup, NodeType } from '@frontend/group/types';
import { Logger } from '@frontend/logger';
import { ProductOrderClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { SettingClient } from '@frontend/setting/api';
import { Setting, SettingKey } from '@frontend/setting/types';
import { ScreenTrigger } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useScreen } from '../useScreen';

export function isProductCategory(element: IProduct | IGroup): element is IGroup {
    return !!(element as IGroup).node_type;
}

export function isProduct(element: IProduct | IGroup): element is IProduct {
    return !isProductCategory(element);
}

export type RequestServicesEmptyState = 'missing_root_category_setting' | 'missing_account' | 'load_failed' | 'no_data';

const ROOT_CATEGORY_SETTING_KEY = SettingKey.DEFAULT_SHOP_PRODUCT_ROOT_CATEGORY_ID;

const getStringSettingValue = (settings: Setting[] | undefined, key: SettingKey): string | null => {
    const value = settings?.find((setting) => setting.key === key)?.value;
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

const useRequestServices = () => {
    const { store, screen_type, updateWizardData, onSubmit, wizardData, navigationState } = useScreen();
    const [elements, setElements] = useState<(IProduct | IGroup)[]>([]);
    const [isLoadingElements, setIsLoadingElements] = useState(false);
    const [fallbackRootCategoryId, setFallbackRootCategoryId] = useState<string | null>(null);
    const [rootCategorySettingResolved, setRootCategorySettingResolved] = useState(false);
    const [isLoadingRootCategorySetting, setIsLoadingRootCategorySetting] = useState(false);
    const [rootCategorySettingLoadFailed, setRootCategorySettingLoadFailed] = useState(false);
    const [catalogLoadFailed, setCatalogLoadFailed] = useState(false);
    const missingRootCategorySettingLogged = useRef(false);
    const [categoryStack, setCategoryStack] = useState<IGroup[]>(() => {
        return store?.data?.requestServicesNavigation?.categoryStack ?? [];
    });

    const submitStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT);
    const selectedServiceProduct = wizardData.selectedServiceProduct as IProduct | undefined;
    const accountId = store?.wizard.account_id ?? navigationState.user?.accountId ?? navigationState.iot?.account_id;

    const storedRootCategoryId = useMemo(() => getStringSettingValue(store?.functionalitySettings, ROOT_CATEGORY_SETTING_KEY), [store?.functionalitySettings]);
    const rootCategoryId = storedRootCategoryId ?? fallbackRootCategoryId;
    const shouldResolveRootCategorySetting = !!store?.functionality.id && !storedRootCategoryId && !fallbackRootCategoryId && !rootCategorySettingResolved;
    const isResolvingRootCategorySetting = shouldResolveRootCategorySetting || isLoadingRootCategorySetting;
    const isLoading = isLoadingElements || isResolvingRootCategorySetting;

    const currentCategoryId = useMemo(() => {
        if (categoryStack.length > 0) return categoryStack[categoryStack.length - 1].id;
        return rootCategoryId;
    }, [categoryStack, rootCategoryId]);

    const currentCategory = useMemo(() => {
        if (categoryStack.length > 0) return categoryStack[categoryStack.length - 1];
        return null;
    }, [categoryStack]);

    const categories = useMemo(() => elements.filter((element): element is IGroup => isProductCategory(element)), [elements]);
    const branchCategories = useMemo(() => categories.filter((category) => category.node_type === NodeType.BRANCH), [categories]);
    const leafCategories = useMemo(() => categories.filter((category) => category.node_type === NodeType.LEAF), [categories]);
    const services = useMemo(() => elements.filter((element): element is IProduct => isProduct(element) && element.type === ProductType.SERVICE), [elements]);

    useEffect(() => {
        if (!shouldResolveRootCategorySetting || !store?.functionality.id) return;

        let isCancelled = false;
        setIsLoadingRootCategorySetting(true);

        SettingClient.fetchSettings({
            entity_type: EntityType.FUNCTIONALITY,
            entity_id: store.functionality.id,
            key: ROOT_CATEGORY_SETTING_KEY
        })
            .then((response) => {
                if (!isCancelled) {
                    setFallbackRootCategoryId(getStringSettingValue(response.results, ROOT_CATEGORY_SETTING_KEY));
                    setRootCategorySettingLoadFailed(false);
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setFallbackRootCategoryId(null);
                    setRootCategorySettingLoadFailed(true);
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setRootCategorySettingResolved(true);
                    setIsLoadingRootCategorySetting(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [shouldResolveRootCategorySetting, store?.functionality.id]);

    useEffect(() => {
        if (isResolvingRootCategorySetting || rootCategorySettingLoadFailed || !accountId || rootCategoryId || missingRootCategorySettingLogged.current) return;

        missingRootCategorySettingLogged.current = true;
        Logger.warn('Request services catalog is not configured: missing functionality setting', {
            key: ROOT_CATEGORY_SETTING_KEY,
            functionalityId: store?.functionality.id,
            accountId
        });
    }, [accountId, isResolvingRootCategorySetting, rootCategoryId, rootCategorySettingLoadFailed, store?.functionality.id]);

    useEffect(() => {
        if (isResolvingRootCategorySetting) return;

        if (!accountId || !currentCategoryId) {
            setElements([]);
            setIsLoadingElements(false);
            return;
        }

        let isCancelled = false;
        setIsLoadingElements(true);
        setCatalogLoadFailed(false);

        DriverAuthenticationManager.getInstance()
            .waitForToken()
            .then((token) => ProductOrderClient.fetchCategoryOrderProducts(accountId, currentCategoryId, { sort_field: 'order', sort_direction: 'asc' }, token))
            .then((response) => {
                if (isCancelled) return;

                const filteredElements = response.results.filter(
                    (element): element is IProduct | IGroup => isProductCategory(element) || element.type === ProductType.SERVICE
                );
                setElements(filteredElements);
                setIsLoadingElements(false);
            })
            .catch(() => {
                if (!isCancelled) {
                    setElements([]);
                    setCatalogLoadFailed(true);
                    setIsLoadingElements(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [accountId, currentCategoryId, isResolvingRootCategorySetting, navigationState.user?.id]);

    const navigateToCategory = useCallback((category: IGroup) => {
        setCategoryStack((previousStack) => [...previousStack, category]);
    }, []);

    const navigateBack = useCallback(() => {
        setCategoryStack((previousStack) => previousStack.slice(0, -1));
    }, []);

    const selectService = useCallback(
        (product: IProduct) => {
            updateWizardData({
                requestServicesNavigation: {
                    categoryStack
                },
                selectedProduct: product,
                selectedServiceProduct: product,
                serviceProductContactSubmitted: false,
                serviceProductOrder: null,
                serviceProductOrderId: null
            });
            onSubmit(submitStep);
        },
        [categoryStack, onSubmit, submitStep, updateWizardData]
    );

    const emptyState: RequestServicesEmptyState = useMemo(() => {
        if (catalogLoadFailed || rootCategorySettingLoadFailed) return 'load_failed';
        if (!accountId) return 'missing_account';
        if (!rootCategoryId) return 'missing_root_category_setting';
        return 'no_data';
    }, [accountId, catalogLoadFailed, rootCategoryId, rootCategorySettingLoadFailed]);

    return {
        branchCategories,
        leafCategories,
        services,
        currentCategory,
        canGoBack: categoryStack.length > 0,
        isLoading,
        emptyState,
        selectedServiceProductId: selectedServiceProduct?.id ?? null,
        navigateToCategory,
        navigateBack,
        onSelectService: selectService
    };
};

export default useRequestServices;
